/**
 * An app's own photos, as the page addresses them.
 *
 * Its own module, with no imports, because two layers need it — the publish request
 * (api/hosting.ts) and the saved record (state/livePage.ts) — and tests stand in for either of
 * those whole modules; a helper living inside one would vanish under the other's mock.
 */

/** One of an app's own photos: the name the app uses for it (`S.media(name)`), and where it is stored. */
export interface MediaAsset {
  id: string;
  url: string;
}

/**
 * The photos an app had, with the ones just added. A new photo under a name already on the list
 * REPLACES it, in its place: the name is the page's only handle on a photo, so two entries under
 * one name would be one photo the server resolves to whichever it met last.
 */
export function mergeAssets<T extends MediaAsset>(had: readonly T[], added: readonly T[]): T[] {
  const byName = new Map(added.map((a) => [a.id, a]));
  const kept = had.map((a) => byName.get(a.id) ?? a);
  const known = new Set(had.map((a) => a.id));
  return [...kept, ...added.filter((a) => !known.has(a.id))];
}
